/**
 * ChatPanel — Reusable message area + input bar
 *
 * The core chat UI used in both ChatApp (embedded in crew modal)
 * and StandaloneChatModal (opened from notification bubble).
 */

import { EventEmitter } from '../../../engine/core/EventEmitter';
import type { StoredMessage } from '../../../db/ChatDatabase';
import type { ChatMessageType } from '@sdl/shared';
import { ImageViewer } from '../ImageViewer';

interface ChatPanelEvents {
  'send': { content: string; type: ChatMessageType };
  'image': void;
  'invite': void;
  'clear': void;
  /** Header avatar/name tapped — open the peer's player card. */
  'profile:tap': { playerId: string };
}

export class ChatPanel extends EventEmitter<ChatPanelEvents> {
  public element: HTMLElement;
  private messagesEl: HTMLElement;
  private inputEl!: HTMLInputElement;
  private localPlayerId: string;
  private peerId: string;
  private peerUsername: string;
  private peerPicture: string;
  /** Object URLs minted for photo blobs — revoked on destroy. */
  private objectUrls: string[] = [];

  constructor(options: {
    localPlayerId: string;
    peerId: string;
    peerUsername: string;
    peerPicture?: string;
  }) {
    super();
    this.localPlayerId = options.localPlayerId;
    this.peerId = options.peerId;
    this.peerUsername = options.peerUsername;
    this.peerPicture = options.peerPicture ?? '';
    this.element = this.createElement();
    this.messagesEl = this.element.querySelector('.chat-messages')!;
    this.inputEl = this.element.querySelector('.chat-text-input')!;
  }

  /** Set initial batch of messages (newest first from DB — reversed for display) */
  setMessages(msgs: StoredMessage[]): void {
    this.messagesEl.innerHTML = '';
    // Messages come newest-first from DB, reverse for chronological display
    const chronological = [...msgs].reverse();
    for (const msg of chronological) {
      this.messagesEl.appendChild(this.createBubble(msg));
    }
    this.scrollToBottom();
  }

  /** Append a single new message */
  appendMessage(msg: StoredMessage): void {
    if (msg.peerId !== this.peerId && msg.from !== this.peerId) return;
    this.messagesEl.appendChild(this.createBubble(msg));
    this.scrollToBottom();
  }

  /** Re-render one bubble in place (e.g. its photo blob just downloaded). */
  updateMessage(msg: StoredMessage): void {
    if (msg.peerId !== this.peerId && msg.from !== this.peerId) return;
    const existing = this.messagesEl.querySelector(`[data-msg-id="${msg.id}"]`);
    if (existing) existing.replaceWith(this.createBubble(msg));
  }

  scrollToBottom(): void {
    requestAnimationFrame(() => {
      this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
    });
  }

  /** Re-pin to the bottom, but only if the player is already near it — a photo
   *  finishing its load must not yank someone who scrolled up to read history.
   *  Threshold covers the max thumbnail height (200px) plus some slack. */
  private scrollToBottomIfPinned(): void {
    const el = this.messagesEl;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distance < 280) this.scrollToBottom();
  }

  focus(): void {
    this.inputEl.focus();
  }

  destroy(): void {
    for (const url of this.objectUrls) URL.revokeObjectURL(url);
    this.objectUrls = [];
    this.element.remove();
    this.removeAllListeners();
  }

  private createElement(): HTMLElement {
    const el = document.createElement('div');
    el.className = 'chat-panel';

    el.innerHTML = `
      <div class="chat-header">
        <button class="chat-header-id" aria-label="View profile">
          <span class="chat-header-avatar">${this.peerPicture
            ? `<img src="${this.peerPicture}" alt="">`
            : `<span>${this.peerUsername.charAt(0).toUpperCase()}</span>`}</span>
          <span class="chat-header-name">${this.peerUsername}</span>
        </button>
        <button class="chat-header-clear" aria-label="Clear chat">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="2">
            <polyline points="3 6 5 6 21 6"/>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
          </svg>
        </button>
      </div>
      <div class="chat-messages"></div>
      <div class="chat-input-bar">
        <button class="chat-input-icon chat-icon-image" aria-label="Send image">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="2">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
            <circle cx="8.5" cy="8.5" r="1.5"/>
            <polyline points="21 15 16 10 5 21"/>
          </svg>
        </button>
        <button class="chat-input-icon chat-icon-invite" aria-label="Send game invite">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="2">
            <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
            <circle cx="8" cy="8" r="1"/>
            <circle cx="16" cy="16" r="1"/>
            <circle cx="12" cy="12" r="1"/>
          </svg>
        </button>
        <input class="chat-text-input" type="text" placeholder="Type a message..." maxlength="500" autocomplete="off" spellcheck="false">
        <button class="chat-send-btn" aria-label="Send">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="22" y1="2" x2="11" y2="13"/>
            <polygon points="22 2 15 22 11 13 2 9 22 2"/>
          </svg>
        </button>
      </div>
    `;

    // Event handlers
    el.querySelector('.chat-header-id')!.addEventListener('click', () => {
      this.emit('profile:tap', { playerId: this.peerId });
    });

    el.querySelector('.chat-header-clear')!.addEventListener('click', () => {
      this.emit('clear', undefined);
    });

    el.querySelector('.chat-icon-image')!.addEventListener('click', () => {
      this.emit('image', undefined);
    });

    el.querySelector('.chat-icon-invite')!.addEventListener('click', () => {
      this.emit('invite', undefined);
    });

    el.querySelector('.chat-send-btn')!.addEventListener('click', () => {
      this.handleSend();
    });

    const input = el.querySelector<HTMLInputElement>('.chat-text-input')!;
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.handleSend();
      }
    });

    return el;
  }

  private handleSend(): void {
    const content = this.inputEl.value.trim();
    if (!content) return;
    this.inputEl.value = '';
    // Dismiss the soft keyboard after sending (blur drops focus → keyboard hides).
    this.inputEl.blur();
    this.emit('send', { content, type: 'text' });
  }

  private createBubble(msg: StoredMessage): HTMLElement {
    const isSelf = msg.from === this.localPlayerId;
    const bubble = document.createElement('div');
    bubble.className = `chat-bubble ${isSelf ? 'chat-bubble-self' : 'chat-bubble-peer'}`;
    bubble.dataset.msgId = msg.id;

    if (msg.type === 'image') {
      if (msg.imageEvicted === 1 && !msg.imageBlob) {
        // Blob was evicted to reclaim space and the CDN copy is long gone.
        bubble.innerHTML = `
          <div class="chat-bubble-image chat-bubble-image-expired">
            <span>📷</span>
            <span>Photo expired</span>
          </div>
          <span class="chat-bubble-time">${this.formatTime(msg.timestamp)}</span>
        `;
        return bubble;
      }
      // Prefer the locally stored bytes; fall back to the CDN URL for the
      // window between delivery and download completion.
      let src = msg.content;
      if (msg.imageBlob) {
        src = URL.createObjectURL(msg.imageBlob);
        this.objectUrls.push(src);
      }
      bubble.innerHTML = `
        <div class="chat-bubble-image">
          <img src="${src}" alt="Photo" loading="lazy">
        </div>
        <span class="chat-bubble-time">${this.formatTime(msg.timestamp)}</span>
      `;
      // Tap the thumbnail → full-screen viewer (same as player card avatars).
      const viewSrc = src;
      const imgEl = bubble.querySelector<HTMLImageElement>('.chat-bubble-image img')!;
      imgEl.addEventListener('click', () => {
        ImageViewer.open(viewSrc);
      });
      // The bubble grows when the image decodes (after append + the initial
      // scroll), pushing the latest message off screen — re-pin on load.
      imgEl.addEventListener('load', () => this.scrollToBottomIfPinned());
    } else if (msg.type === 'invite') {
      bubble.innerHTML = `
        <div class="chat-bubble-invite">
          <span class="chat-invite-icon">🎲</span>
          <span class="chat-invite-text">${msg.content}</span>
        </div>
        <span class="chat-bubble-time">${this.formatTime(msg.timestamp)}</span>
      `;
    } else {
      bubble.innerHTML = `
        <div class="chat-bubble-text">${this.escapeHtml(msg.content)}</div>
        <span class="chat-bubble-time">${this.formatTime(msg.timestamp)}</span>
      `;
    }

    return bubble;
  }

  private formatTime(ts: number): string {
    const d = new Date(ts);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  private escapeHtml(str: string): string {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}
